import { styled, TextField as _Input, TextFieldProps } from '@mui/material';
import { useEffect } from 'react';
import { Control, UseFormRegisterReturn, useWatch } from 'react-hook-form';
import { useIMask, IMask } from 'react-imask';

export type CustomInputProps = TextFieldProps & {
  endIcon?: JSX.Element;
  startIcon?: JSX.Element;
  validationRegister?: UseFormRegisterReturn;
  control?: Control;
  mask?: IMask.AnyMaskedOptions;
  // TODO: Improve that implementation by changing lib
  /** Must use it on use complex mask options */
  textArea?: boolean;
};

const StyledInput = styled(_Input, { shouldForwardProp: (prop) => prop !== 'textArea' })<{ textArea?: boolean }>(
  ({ theme, textArea }) => ({
    color: theme.palette.neutral[100],
    '& ::-webkit-scrollbar': {
      height: '8px',
      width: '6px',
    },
    '& ::-webkit-scrollbar-track-piece': {
      background: theme.palette.neutral[300],
    },
    '& ::-webkit-scrollbar-thumb:vertical, & ::-webkit-scrollbar-thumb:horizontal': {
      background: theme.palette.neutral[400],
      borderRadius: '20px',
    },
    '& .MuiInputBase-inputMultiline': {
      resize: textArea ? 'vertical' : 'none',
    },
    '& .MuiFilledInput-input:-webkit-autofill': {
      WebkitBoxShadow: `0 0 0 ${theme.palette.neutral[200]} inset`,
      WebkitTransitionDelay: '99999s',
    },
    '& .MuiFilledInput-root': {
      background: theme.palette.neutral[400],
    },
    '& .Mui-error:after': {
      borderBottomColor: `${theme.palette.primary.main} !important`,
    },
    '& .MuiFormLabel-root.Mui-error': {
      color: theme.palette.primary.main,
    },
    '& .MuiFormHelperText-root': {
      height: theme.spacing(3),
    },
    '& .MuiFormHelperText-root.Mui-error': {
      color: theme.palette.primary.main,
    },
  })
);

export const Input = ({
  startIcon,
  endIcon,
  variant,
  validationRegister,
  control,
  mask,
  ...props
}: CustomInputProps): JSX.Element => {
  const defaultValue =
    validationRegister && control
      ? // eslint-disable-next-line react-hooks/rules-of-hooks
        (useWatch({ control, name: validationRegister.name }) as string) || ''
      : (props.value as string) || undefined;

  if (mask) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { ref, maskRef, setValue, setUnmaskedValue } = useIMask(mask, {
      onComplete: (value) => {
        setValue(value);
      },
    });

    // eslint-disable-next-line react-hooks/rules-of-hooks
    useEffect(() => {
      setUnmaskedValue(defaultValue || '');
      setValue(defaultValue || '');

      ref.current.value = defaultValue || '';
      maskRef.current.value = defaultValue || '';
    }, [defaultValue, maskRef, ref, setUnmaskedValue, setValue]);

    return (
      <StyledInput
        {...props}
        {...validationRegister}
        InputLabelProps={{ shrink: true }}
        inputRef={ref}
        InputProps={props.InputProps ? { ...props.InputProps } : { startAdornment: startIcon, endAdornment: endIcon }}
        variant={variant || 'filled'}
      />
    );
  }

  return (
    <StyledInput
      {...props}
      InputLabelProps={{ shrink: true }}
      InputProps={props.InputProps ? { ...props.InputProps } : { startAdornment: startIcon, endAdornment: endIcon }}
      variant={variant || 'filled'}
      value={defaultValue}
      {...validationRegister}
    />
  );
};

export default Input;
